import moment from "moment";
import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";

import { formatName } from "../../../../common/format-name";
import { PatientService } from "../../../../services/patient.service";
import { HosDiscipline } from "../../../../enums/HosDisciplineEnum";
import { PalDiscipline } from "../../../../enums/PalDisciplineEnum";
import { ApplicationEnum } from "../../../../enums/ApplicationEnum";
import { enumToMap } from "../../../../common/vue-helpers/modifiers/enum-modifiers";
import { ITypeaheadOptions } from "../../../../resources-vue/vue-interfaces/i-typeahead";
import { EnumsService } from "../../../../services/enums.service";
import config from "../../../../common/config";
import { TaskService } from "../../../../services/task.service";
import { NavigationService } from "../../../../services/navigation.service";
import { formatDate } from "../../../../common/vue-helpers/modifiers/value-modifier";
import { BranchesService } from "../../../../services/branches-service";
import { PermissionManager } from "../../../../common/utilities/permission-manager";
import { PatientStatus } from "../../../../enums/PatientStatusEnum";

import ErrorMessageHandler from "../../../../resources-vue/vue-custom-elements/ErrorMessageHandler/ErrorMessageHandler.vue";
import HosPalProfileDetails from "./HosPalProfileDetails/HosPalProfileDetails.vue";
import HosPalTaskRow from "./HosPalTaskRow/HosPalTaskRow.vue";
import SortableTh from "../../../../resources-vue/vue-custom-elements/SortableTh/SortableTh.vue";

export default defineComponent({
    components: {
        ErrorMessageHandler,
        HosPalProfileDetails,
        HosPalTaskRow,
        SortableTh
    },

    props: {
        selectedPatientDetails: {
            default: null
        },
        agencyId: {
            type: String
        },
        application: {
            type: Number
        },
        patientId: {
            type: String
        }
    },
    data(vm) {
        return {
            _permissionManager: null as PermissionManager,
            _branchesService: null as BranchesService,
            _navigationService: null as NavigationService,
            _taskService: null as TaskService,
            _enumService: null as EnumsService,
            _patientService: null as PatientService,
            statusEnum: PatientStatus,
            hosDisciplines: enumToMap(HosDiscipline),
            palDisciplines: enumToMap(PalDiscipline),
            benefitPeriods: [],
            selectedDiscipline: null as number,
            scheduleStatus: [] as ITypeaheadOptions[],
            selectedStatus: null as number,
            isLoading: false as boolean,
            isError: false as boolean,
            pageSize: 10 as number,
            columns: 7 as number,
            allTasks: [],
            selectedBenefitPeriod: "All" as any,
            selectedAdmissionPeriod: null as any,
            sortBy: "TaskName",
            sortOrder: "asc",
            moment,
            formatName,
            ApplicationEnum,
            formatDate
        };
    },
    async created() {
        this._patientService = Container.instance.get(PatientService);
        this._enumService = Container.instance.get(EnumsService);
        this._taskService = Container.instance.get(TaskService);
        this._navigationService = Container.instance.get(NavigationService);
        this._branchesService = Container.instance.get(BranchesService);
        this._permissionManager = Container.instance.get(PermissionManager);
    },
    mounted() {
        this.getBenefitPeriods();
        this.getScheduleStatus();
    },
    computed: {
        hasData() {
            return this.allTasks.length > 0;
        },
        loadingCount() {
            return this.allTasks.length > 0 ? this.allTasks.length : config.pageSize;
        }
    },

    watch: {
        selectedBenefitPeriod: {
            handler() {
                this.getPatientTasks();
            },
            deep: true
        },
        selectedDiscipline: {
            handler() {
                this.getPatientTasks();
            },
            deep: true
        },
        selectedStatus: {
            handler() {
                this.getPatientTasks();
            },
            deep: true
        }
    },

    methods: {
        async getBenefitPeriods() {
            const payload = {
                agencyId: this.agencyId,
                patientId: this.patientId,
                application: this.application
            };
            let response = (await this._patientService.getBenefitPeriods(payload)) as any;
            response = response.map((data) => {
                return {
                    ...data,
                    name: `${moment(data.startDate).format("YYYY/MM/DD")} - ${moment(data.endDate).format("YYYY/MM/DD")}`,
                    value: data.id
                };
            });
            this.benefitPeriods = response;
            const activePeriod = this.benefitPeriods.find((period) => {
                let today = moment();
                let periodStartDate = period.isTransfer ? period.transferBenefitPeriodStartDate : period.startDate;
                let startDate = moment(periodStartDate);
                let endDate = moment(period.endDate);
                return today.isBetween(startDate, endDate, "day", "[]");
            });
            if (activePeriod && this.selectedPatientDetails.status === this.statusEnum.enum.active) {
                this.selectedBenefitPeriod = activePeriod;
            }
        },
        async getScheduleStatus() {
            this.scheduleStatus = (await this._enumService.getScheduleStatus()) as any;
        },
        async getPatientTasks() {
            try {
                this.isLoading = true;
                const payload = {
                    AgencyId: this.agencyId,
                    PatientId: this.patientId,
                    StartDate:
                        this.selectedBenefitPeriod === "PendingAdmissionActivity"
                            ? moment().format("YYYY-MM-DD")
                            : this.selectedBenefitPeriod === "All"
                              ? this.selectedPatientDetails.admissionDate
                              : this.selectedBenefitPeriod.startDate,
                    EndDate:
                        this.selectedBenefitPeriod === "PendingAdmissionActivity"
                            ? moment().format("YYYY-MM-DD")
                            : this.selectedBenefitPeriod === "All"
                              ? ""
                              : this.selectedBenefitPeriod.endDate,
                    Order: this.sortOrder,
                    SortBy: this.sortBy,
                    Discipline: this.selectedDiscipline ? this.selectedDiscipline : null,
                    Status: this.selectedStatus ? this.selectedStatus : null,
                    DateRangeId: this.selectedBenefitPeriod === "All" ? "All" : ""
                };
                this.allTasks = (await this._taskService.getTasksByPatient(payload)) as any;
            } catch (err) {
                this.isError = true;
                console.error(err);
            } finally {
                this.isLoading = false;
            }
        },

        getBenefitPeriodLabel(period) {
            let today = moment();
            let periodStartDate = period.isTransfer ? period.transferBenefitPeriodStartDate : period.startDate;
            let startDate = moment(periodStartDate);
            let endDate = moment(period.endDate);
            let dateRange = `${startDate.format("MM/DD/YYYY")} - ${endDate.format("MM/DD/YYYY")}`;
            if (today.isBetween(startDate, endDate, "day", "[]")) {
                if (this.application === ApplicationEnum.AxxessHospiceFE) {
                    return `This Benefit Period (${dateRange})`;
                } else {
                    return `This Admission Period (${dateRange})`;
                }
            } else {
                return dateRange;
            }
        },
        sortByUpdated(value: string) {
            if (value != this.sortBy) {
                this.sortBy = value;
                this.getPatientTasks();
            }
        },

        sortOrderUpdated(value: string) {
            if (value != this.sortOrder) {
                this.sortOrder = value;
                this.getPatientTasks();
            }
        }
    }
});
